import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';

import { Loading } from '@/components/shared';
import {
  FancyMultiSelectPopover,
  type Options,
} from '@/components/ui/fancy-multi-select-popover';
import { Button } from '@/components/ui/NewButton';
import { SettingRow } from '@/components/ui/settings-panel';
import { useDictationLanguages } from '@/hooks/useDictationLanguages';
import { apiErrorMessage } from '@/lib/api-client';
import { useMatterLabels } from '@/lib/useMatterLabels';

// Base language subtag of a locale, e.g. 'fr' for both 'fr-FR' and 'fr-CA'.
function baseLanguage(code: string): string {
  return code.split('-', 1)[0].toLowerCase();
}

export function DictationLanguages() {
  const { data, isLoading, isError, refetch, save, isSaving } = useDictationLanguages();
  const label = useMatterLabels();

  const labelForCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of data?.available ?? []) map.set(opt.code, opt.label);
    return (code: string) => map.get(code) ?? code;
  }, [data?.available]);

  const [selected, setSelected] = useState<Options[]>([]);

  // Azure auto-detection can't disambiguate two locales of the same language, so once a
  // base language is chosen (e.g. fr-FR) hide its siblings (fr-CA). The already-selected
  // option stays available so it still renders/toggles correctly.
  const options: Options[] = useMemo(() => {
    const selectedValues = new Set(selected.map((opt) => opt.value));
    const selectedBases = new Set(selected.map((opt) => baseLanguage(opt.value)));
    return (data?.available ?? [])
      .filter(
        (opt) => selectedValues.has(opt.code) || !selectedBases.has(baseLanguage(opt.code)),
      )
      .map((opt) => ({ value: opt.code, label: opt.label }));
  }, [data?.available, selected]);

  // Keep the local selection in sync with whatever the server last confirmed.
  useEffect(() => {
    if (!data) return;
    setSelected(data.selected.map((code) => ({ value: code, label: labelForCode(code) })));
  }, [data, labelForCode]);

  const handleChange = async (next: Options[]) => {
    const previous = selected;
    setSelected(next);
    try {
      await save(next.map((opt) => opt.value));
    } catch (err) {
      setSelected(previous);
      toast.error(apiErrorMessage(err) || 'Failed to update dictation languages');
    }
  };

  const handleRetry = async () => {
    const result = await refetch();
    if (result.error) {
      toast.error(apiErrorMessage(result.error) || 'Failed to load dictation languages');
    }
  };

  const atLimit = data ? selected.length >= data.max_additional : false;

  return (
    <SettingRow
      label="Voice dictation languages"
      description={
        data
          ? `Your default is ${data.default_label}. Add other languages you speak and Case Partner will auto-detect them while you dictate.`
          : label('dictation-languages-empty-hint')
      }
    >
      {isLoading ? (
        <div className="flex h-9 items-center">
          <Loading text="Loading dictation languages" />
        </div>
      ) : isError || !data ? (
        <div className="flex flex-col items-start gap-3">
          <p className="text-sm text-muted-foreground">
            Dictation languages could not be loaded.
          </p>
          <Button
            variant="outline"
            size="sm"
            text="Retry"
            onClick={() => {
              void handleRetry();
            }}
          />
        </div>
      ) : (
        <div className="space-y-2">
          <FancyMultiSelectPopover
            value={selected}
            onValueChange={(next) => {
              void handleChange(next);
            }}
            options={options}
            placeholder={atLimit ? 'Language limit reached' : 'Add a language…'}
            maxItems={data.max_additional}
          />
          <p className="text-xs text-muted-foreground">
            {atLimit
              ? `You've added the maximum of ${data.max_additional} additional languages.`
              : `Add up to ${data.max_additional} additional languages.`}
            {isSaving ? ' Saving…' : ''}
          </p>
        </div>
      )}
    </SettingRow>
  );
}
